import { alpha } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { Theme as MuiTheme } from "@mui/material";

export const getFormSectionStyles = (theme: MuiTheme) => {
  const isDark = theme.palette.mode === "dark";
  const accent = theme.palette.status.accent;
  const cardBg = isDark ? theme.palette.background.paper : theme.palette.common.white;
  const cardBorder = isDark
    ? alpha(theme.palette.common.white, 0.14)
    : alpha(theme.palette.common.black, 0.12);
  const railBg = alpha(accent, isDark ? 0.12 : 0.05);
  const railBorder = alpha(accent, isDark ? 0.75 : 0.85);

  // --- Polished tokens (used by the refreshed ClientProcessForm / wrappers) ---
  const surfaceBg = isDark ? "#141416" : "#fff";
  const surfaceBorder = alpha(isDark ? "#fff" : "#000", isDark ? 0.06 : 0.08);
  const surfaceSubtleBg = alpha(isDark ? "#fff" : "#000", isDark ? 0.025 : 0.02);
  const surfaceDivider = alpha(isDark ? "#fff" : "#000", 0.06);
  const hoverBg = alpha(isDark ? "#fff" : "#000", 0.04);

  const surfaceCard: SxProps<Theme> = {
    borderRadius: "18px",
    border: `1px solid ${surfaceBorder}`,
    backgroundColor: surfaceBg,
    backgroundImage: isDark
      ? `linear-gradient(180deg, ${alpha("#fff", 0.025)} 0%, ${alpha("#fff", 0)} 38%)`
      : "none",
    boxShadow: isDark
      ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 12px 32px -16px rgba(0,0,0,0.6)"
      : "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -20px rgba(15,23,42,0.12)",
  };

  const eyebrow: SxProps<Theme> = {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "text.secondary",
    display: "block",
  };

  const inputField: SxProps<Theme> = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: surfaceSubtleBg,
      transition: "box-shadow 140ms ease, background-color 140ms ease",
      "&:hover": {
        backgroundColor: alpha(isDark ? "#fff" : "#000", isDark ? 0.04 : 0.025),
      },
      "&.Mui-focused": {
        backgroundColor: isDark ? alpha("#fff", 0.04) : "#fff",
        boxShadow: `0 0 0 3px ${alpha(accent, 0.18)}`,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(isDark ? "#fff" : "#000", 0.12),
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: alpha(isDark ? "#fff" : "#000", 0.22),
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: accent,
        borderWidth: 1,
      },
    },
    "& .MuiInputBase-inputMultiline": {
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: "12.5px",
      lineHeight: 1.55,
    },
    "& .MuiFormHelperText-root": {
      marginLeft: 0.5,
      fontSize: "11.5px",
    },
  };

  // Segmented "pill" tabs (Linear / AWS Console style)
  const segmentedTabs: SxProps<Theme> = {
    backgroundColor: alpha(isDark ? "#fff" : "#000", 0.03),
    borderRadius: "10px",
    p: 0.5,
    minHeight: 0,
    "& .MuiTabs-flexContainer": { gap: 0.5 },
    "& .MuiTabs-indicator": { display: "none" },
    "& .MuiTab-root": {
      minHeight: 32,
      px: 1.75,
      py: 0.75,
      borderRadius: "8px",
      textTransform: "none",
      fontWeight: 500,
      fontSize: "13px",
      letterSpacing: 0,
      color: "text.secondary",
      transition: "background-color 140ms ease, color 140ms ease",
      "&:hover": {
        backgroundColor: hoverBg,
        color: "text.primary",
      },
      "&.Mui-selected": {
        backgroundColor: isDark ? "#1d1d20" : "#fff",
        color: "text.primary",
        fontWeight: 600,
        boxShadow: isDark
          ? `0 1px 0 ${alpha("#fff", 0.08)} inset, 0 1px 3px rgba(0,0,0,0.4)`
          : "0 1px 2px rgba(15,23,42,0.08), 0 0 0 1px rgba(15,23,42,0.06)",
      },
    },
    "& .MuiTabScrollButton-root": {
      width: 28,
      color: "text.secondary",
      "&.Mui-disabled": { opacity: 0.3 },
    },
  };

  const toggleRow: SxProps<Theme> = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    px: 1.75,
    py: 1.25,
    borderRadius: "10px",
    border: `1px solid ${surfaceBorder}`,
    backgroundColor: surfaceSubtleBg,
    transition: "border-color 140ms ease, background-color 140ms ease",
    "&:hover": {
      borderColor: alpha(isDark ? "#fff" : "#000", 0.16),
    },
  };

  const switchControl: SxProps<Theme> = {
    "& .MuiSwitch-track": {
      backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.32 : 0.22),
      opacity: 1,
    },
    "& .MuiSwitch-switchBase.Mui-checked": { color: "#fff" },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: accent,
      opacity: 1,
    },
  };

  const primaryButton: SxProps<Theme> = {
    backgroundColor: accent,
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: "10px",
    px: 2.5,
    py: 0.9,
    boxShadow: `0 1px 0 ${alpha("#fff", 0.06)} inset, 0 6px 16px -8px ${alpha(accent, 0.6)}`,
    transition: "box-shadow 160ms ease",
    "&:hover": {
      backgroundColor: accent,
      boxShadow: `0 0 0 4px ${alpha(accent, 0.18)}, 0 8px 24px -8px ${alpha(accent, 0.7)}`,
    },
    "&:focus-visible": {
      outline: "none",
      boxShadow: `0 0 0 4px ${alpha(accent, 0.32)}`,
    },
    "&.Mui-disabled": {
      backgroundColor: alpha(accent, 0.4),
      color: alpha("#fff", 0.7),
      boxShadow: "none",
    },
  };

  const ghostButton: SxProps<Theme> = {
    textTransform: "none",
    borderRadius: "10px",
    fontWeight: 500,
    color: "text.secondary",
    border: `1px solid ${alpha(isDark ? "#fff" : "#000", 0.1)}`,
    px: 2,
    py: 0.75,
    backgroundColor: "transparent",
    "&:hover": {
      color: "text.primary",
      borderColor: alpha(isDark ? "#fff" : "#000", 0.2),
      backgroundColor: hoverBg,
    },
  };

  const dangerGhostButton: SxProps<Theme> = {
    textTransform: "none",
    borderRadius: "10px",
    fontWeight: 500,
    color: theme.palette.status.error,
    border: `1px solid ${alpha(theme.palette.status.error, 0.35)}`,
    px: 2,
    py: 0.75,
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: alpha(theme.palette.status.error, 0.6),
      backgroundColor: alpha(theme.palette.status.error, isDark ? 0.1 : 0.06),
    },
  };

  return {
    accent,
    // legacy tokens kept for AssetProcessingForm / AssetStorageForm / AssetSyndicationForm
    paper: {
      p: 3,
      borderRadius: 2,
      backgroundColor: cardBg,
      border: `1px solid ${cardBorder}`,
      boxShadow: isDark
        ? `0 1px 0 ${alpha(theme.palette.common.white, 0.06)}`
        : `0 1px 2px ${alpha(theme.palette.common.black, 0.08)}`,
    },
    title: {
      mb: 2,
      fontWeight: 600,
      color: accent,
      letterSpacing: 0.2,
    },
    checkbox: {
      color: theme.palette.text.secondary,
      "&.Mui-checked": { color: accent },
      "&:hover": { backgroundColor: alpha(accent, 0.08) },
    },
    switch: {
      "& .MuiSwitch-track": {
        backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.35 : 0.25),
      },
      "& .MuiSwitch-switchBase.Mui-checked": { color: accent },
      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: accent,
      },
    },
    nestedSection: {
      p: 2,
      mb: 3,
      borderLeft: `3px solid ${railBorder}`,
      borderRadius: 1,
      backgroundColor: railBg,
    },
    collapsibleRail: {
      pl: 3,
      borderLeft: `2px solid ${railBorder}`,
      backgroundColor: railBg,
      borderTopRightRadius: 6,
      borderBottomRightRadius: 6,
      mt: 1,
      ml: 0.5,
      mb: 3,
    },
    // polished tokens for refreshed forms
    surfaceCard,
    surfaceBorder,
    surfaceDivider,
    eyebrow,
    inputField,
    segmentedTabs,
    toggleRow,
    switchControl,
    primaryButton,
    ghostButton,
    dangerGhostButton,
  };
};
